/**
 * Frontend capability mirror of the backend `config/capabilities.py` (DEC-019 Step ③ Step 6).
 *
 * The BACKEND is the authority — these tables must stay in lockstep with `ACCOUNT_TYPE_CAPS` /
 * `ORG_POSITION_CAPS` there. This module is pure (no React) so it can be unit-tested and reused.
 * The React binding (with the anti-flash `loadedOnce` gating) lives in `hooks/useCapabilities.ts`.
 *
 * Layers: L2 account-type (profiles.role; `admin` = superset) and L3 org-position (profiles.org_role:
 * `licensing.act`, `reference_imports.manage`, `org.manage`). L4 `access_mode` (per-resource licensing
 * relationship) is NOT modeled here — those gates stay in the licensing components.
 */

// ── L2 account-type capabilities ───────────────────────────────────────────────
export const CATALOG_READ = "catalog.read";
export const CATALOG_READ_PUBLISHER_DIRECTORY = "catalog.read.publisher_directory";
export const CATALOG_WRITE = "catalog.write";
export const CATALOG_UPLOAD = "catalog.upload";
export const STOREFRONT_READ = "storefront.read";
export const STOREFRONT_WRITE = "storefront.write";
export const SEARCH_RUN = "search.run";
export const REQUEST_CREATE = "request.create";
export const CONFIDENTIAL_USE = "confidential.use";
export const LICENSING_PARTICIPATE = "licensing.participate";
export const RIGHTS_READ = "rights.read";
export const RIGHTS_READ_CONFIRMED = "rights.read.confirmed";
export const RIGHTS_WRITE = "rights.write";
export const RIGHTS_ENRICH = "rights.enrich";
export const LICENSING_READ_HIDDEN_BUDGET = "licensing.read.hidden_budget";
export const LICENSING_READ_HOLDER_THREADS = "licensing.read.holder_threads";
export const NOTIFICATIONS_READ = "notifications.read";
export const ADMIN_MANAGE = "admin.manage";
// Reference import capabilities (Disco playlist import — buyer + admin only at L2).
// VIEW + MANAGE both exist to stay in lockstep with backend config/capabilities.py (_BUYER_CAPS
// lists both). Gates today use MANAGE (L2 AND L3); VIEW is the account-type read grant.
export const REFERENCE_IMPORTS_VIEW = "reference_imports.view";
export const REFERENCE_IMPORTS_MANAGE = "reference_imports.manage";
// Receiving deal money: the organisation's Stripe payout account and its holder W-9 / W-8. Held by
// every account type a deal can pay (seller, negotiator; admin as the superset) — never a buyer.
export const PAYOUTS_MANAGE = "payouts.manage";

// ── L3 org-position capabilities ───────────────────────────────────────────────
export const LICENSING_ACT = "licensing.act";
// Editing the organization's own details (about, city, countries, website, contacts, address, IPI)
// through PATCH /api/organizations/{id}. Held by the org's owner and admin POSITIONS; a platform admin
// holds it at L2 through the superset. Name, type and tax ID stay platform-admin-only on the server.
// Gate UI on `canManageOrganization`, which adds the backend's other conditions.
export const ORG_MANAGE = "org.manage";

export type Capability = string;

const BUYER_CAPS: ReadonlySet<Capability> = new Set([
  CATALOG_READ,
  STOREFRONT_READ,
  SEARCH_RUN,
  REQUEST_CREATE,
  CONFIDENTIAL_USE,
  LICENSING_PARTICIPATE,
  RIGHTS_READ_CONFIRMED, // buyer is restricted to confirmed rights/credits rows
  LICENSING_READ_HIDDEN_BUDGET,
  NOTIFICATIONS_READ,
  REFERENCE_IMPORTS_VIEW,
  REFERENCE_IMPORTS_MANAGE,
]);

const SELLER_CAPS: ReadonlySet<Capability> = new Set([
  CATALOG_READ,
  CATALOG_READ_PUBLISHER_DIRECTORY,
  CATALOG_WRITE,
  CATALOG_UPLOAD,
  STOREFRONT_READ,
  STOREFRONT_WRITE,
  SEARCH_RUN,
  LICENSING_PARTICIPATE,
  RIGHTS_READ,
  RIGHTS_WRITE,
  RIGHTS_ENRICH,
  LICENSING_READ_HOLDER_THREADS,
  NOTIFICATIONS_READ,
  PAYOUTS_MANAGE,
]);

// Negotiator = reduced seller: licensing participation + rights, NO catalog/storefront/upload/search.
// rights.write is a SURFACE grant; the actual mutation stays L4-gated by the song-request relationship.
const NEGOTIATOR_CAPS: ReadonlySet<Capability> = new Set([
  LICENSING_PARTICIPATE,
  RIGHTS_READ,
  RIGHTS_WRITE,
  LICENSING_READ_HOLDER_THREADS,
  NOTIFICATIONS_READ,
  PAYOUTS_MANAGE, // an invited holder is paid on its own side
]);

// Every L2 capability — admin is a SUPERSET (mirrors backend `_ADMIN_CAPS`, which is every capability
// but LICENSING_ACT). ORG_MANAGE is an L3 position capability that the admin superset also holds.
export const ALL_L2_CAPABILITIES: ReadonlySet<Capability> = new Set([
  ...BUYER_CAPS,
  ...SELLER_CAPS,
  ...NEGOTIATOR_CAPS,
  ADMIN_MANAGE,
  ORG_MANAGE,
]);

export const ACCOUNT_TYPE_CAPS: Readonly<Record<string, ReadonlySet<Capability>>> = {
  buyer: BUYER_CAPS,
  seller: SELLER_CAPS,
  negotiator: NEGOTIATOR_CAPS,
  admin: ALL_L2_CAPABILITIES, // superset
};

export const ORG_POSITION_CAPS: Readonly<Record<string, ReadonlySet<Capability>>> = {
  owner: new Set([LICENSING_ACT, REFERENCE_IMPORTS_MANAGE, ORG_MANAGE]),
  admin: new Set([LICENSING_ACT, REFERENCE_IMPORTS_MANAGE, ORG_MANAGE]),
  editor: new Set([LICENSING_ACT, REFERENCE_IMPORTS_MANAGE]),
  member: new Set([LICENSING_ACT, REFERENCE_IMPORTS_MANAGE]),
  viewer: new Set(),
  billing: new Set(),
};

/** Does this account-type role hold the L2 capability? Unknown/null role ⇒ false (default-DENY). */
export function roleHasCapability(role: string | null | undefined, cap: Capability): boolean {
  if (!role) return false;
  return ACCOUNT_TYPE_CAPS[role]?.has(cap) ?? false;
}

/** Does this org position hold the L3 capability? Unknown/null position ⇒ false (default-DENY). */
export function orgRoleHasCapability(orgRole: string | null | undefined, cap: Capability): boolean {
  if (!orgRole) return false;
  return ORG_POSITION_CAPS[orgRole]?.has(cap) ?? false;
}

/** The resolved subject — what the capability check is evaluated against. */
export interface Subject {
  userId: string | null;
  accountType: string | null; // profiles.role
  orgRole: string | null; // profiles.org_role
  organizationId: string | null; // profiles.organization_id
  labelTag: string | null;
}

/**
 * Pure capability check against a resolved subject: holds the cap if the account type (L2) OR the org
 * position (L3) grants it. A name can sit on both layers (`reference_imports.manage`, `org.manage`), so
 * a gate that needs both, or more than the position, has its own helper below.
 * Callers must only trust this once the subject is resolved (see `useCapabilities().loadedOnce`).
 */
export function subjectHas(subject: Subject, cap: Capability): boolean {
  return roleHasCapability(subject.accountType, cap) || orgRoleHasCapability(subject.orgRole, cap);
}

/**
 * Can this subject manage reference imports (Disco playlist import)?
 *
 * Requires L2 AND L3 — BOTH must hold:
 *   L2: account type must be buyer (or admin) → `roleHasCapability(accountType, "reference_imports.manage")`
 *   L3: org position must be actionable (owner/admin/editor/member, NOT viewer/billing) →
 *       `orgRoleHasCapability(orgRole, "reference_imports.manage")`
 *
 * Do NOT use `subjectHas()` (which is L2 OR L3) or `has("reference_imports.manage")` for this gate —
 * an org-position alone without a buyer account type must NOT grant access, and a buyer with viewer
 * org-position must be denied write operations. The backend enforces the same AND logic.
 *
 * Gate UI behind `useCapabilities().loadedOnce` before calling this.
 */
export function canManageReferenceImports(subject: Subject): boolean {
  return (
    roleHasCapability(subject.accountType, REFERENCE_IMPORTS_MANAGE) &&
    orgRoleHasCapability(subject.orgRole, REFERENCE_IMPORTS_MANAGE)
  );
}

/**
 * Can this account's organisation be PAID on a deal, and so be shown payout setup and the holder
 * W-9 / W-8? The account-type half of the rule (`payouts.manage`, which the backend checks on every
 * payout and holder-tax endpoint); whether THIS deal pays the org is the deal page's own question.
 * A buyer's organisation pays; it is never a payee.
 *
 * Trust it only once `useCapabilities().loadedOnce` — before that the subject is not resolved.
 */
export function canReceivePayouts(subject: Subject): boolean {
  return roleHasCapability(subject.accountType, PAYOUTS_MANAGE);
}

/** The account types the capability map knows. A position grants nothing to any other (or none). */
const KNOWN_ACCOUNT_TYPES: ReadonlySet<string> = new Set(Object.keys(ACCOUNT_TYPE_CAPS));

/**
 * May this account edit its OWN organization's details (`org.manage`)? The backend rule
 * (`organization_service._assert_can_manage_organization`), mirrored exactly:
 *   - a platform admin (the L2 superset), or
 *   - the org's owner or admin POSITION, held by a known account type (buyer, seller, negotiator,
 *     admin). A NULL position is no permission; editor, member, viewer and billing are read-only.
 * And there must be an organization to edit. Name, type and tax ID are never editable here.
 *
 * Do NOT use `has(ORG_MANAGE)` for this gate: it is L2 OR L3, so it would admit a position held by an
 * unknown account type. Trust it only once `useCapabilities().loadedOnce`.
 */
export function canManageOrganization(subject: Subject): boolean {
  if (!subject.organizationId || !subject.accountType || !KNOWN_ACCOUNT_TYPES.has(subject.accountType)) {
    return false;
  }
  return (
    roleHasCapability(subject.accountType, ORG_MANAGE) ||
    orgRoleHasCapability(subject.orgRole, ORG_MANAGE)
  );
}
